import './code-highlight-block.css';

import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useMemo, createElement } from 'react';
import rehypeHighlight from 'rehype-highlight';

import Link from '@mui/material/Link';

import { isExternalLink } from 'src/routes/utils';
import { RouterLink } from 'src/routes/components';

import { Image } from '../image';
import { StyledRoot } from './styles';
import { markdownClasses } from './classes';
import { MermaidDiagram } from './mermaid-diagram';
import { markdownTableComponents } from './markdown-table';
import { htmlToMarkdown, isMarkdownContent } from './html-to-markdown';

// ----------------------------------------------------------------------

export function Markdown({ children, sx, raw, compactTables = false, ellipsisAt = 0, ...other }) {
  const content = useMemo(() => {
    const resolved = raw || isMarkdownContent(`${children}`)
      ? `${children ?? ''}`.trim()
      : htmlToMarkdown(`${children}`.trim());

    if (ellipsisAt > 0) {
      const plainText = markdownToPlainText(resolved);
      if (plainText.length > ellipsisAt) {
        return `${plainText.slice(0, ellipsisAt).trimEnd()}...`;
      }
      return plainText;
    }

    return sanitizeMarkdownHeadings(resolved);
  }, [children, ellipsisAt, raw]);

  const resolvedComponents = useMemo(
    () =>
      compactTables
        ? {
          ...components,
          table: (props) =>
            createElement(markdownTableComponents.table, { ...props, compact: true }),
        }
        : components,
    [compactTables]
  );

  return (
    <StyledRoot
      children={content}
      components={resolvedComponents}
      remarkPlugins={[remarkGfm]}
      rehypePlugins={rehypePlugins}
      /* base64-encoded images
       * https://github.com/remarkjs/react-markdown/issues/774
       * urlTransform={(value: string) => value}
       */
      className={markdownClasses.root}
      sx={sx}
      {...other}
    />
  );
}

const rehypePlugins = [rehypeRaw, rehypeHighlight];

// Strip leading "? " artifacts from headings (e.g. "# ? Title" → "# Title")
function sanitizeMarkdownHeadings(content) {
  return content.replace(/^(#{1,6}\s)\?\s+/gm, '$1');
}

function markdownToPlainText(value) {
  if (!value) return '';

  return (
    `${value}`
      // fenced code blocks -> keep inner text
      .replace(/```[\w-]*\n([\s\S]*?)```/g, '$1')
      // inline code
      .replace(/`([^`]+)`/g, '$1')
      // images ![alt](url) -> alt
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
      // links [text](url) -> text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // emphasis markers
      .replace(/(\*\*|__|\*|_|~~)/g, '')
      // headings/blockquote/list markers
      .replace(/^\s{0,3}(#{1,6}|\*|-|\+|>|\d+\.)\s+/gm, '')
      // collapse whitespace
      .replace(/\s+/g, ' ')
      .trim()
  );
}

const components = {
  ...markdownTableComponents,
  img: ({ node, ...other }) => (
    <Image
      ratio="16/9"
      className={markdownClasses.content.image}
      sx={{ borderRadius: 2 }}
      {...other}
    />
  ),
  a: ({ href, children, node, ...other }) => {
    const linkProps = isExternalLink(href)
      ? { target: '_blank', rel: 'noopener' }
      : { component: RouterLink };

    return (
      <Link {...linkProps} href={href} className={markdownClasses.content.link} {...other}>
        {children}
      </Link>
    );
  },
  pre: ({ children, node }) => {
    const codeNode = node?.children?.find((child) => child.tagName === 'code');
    const codeClassName = codeNode?.properties?.className;
    const isMermaid = Array.isArray(codeClassName) && codeClassName.includes('language-mermaid');

    // Mermaid diagrams render as a centered SVG, not a dark scrollable code block.
    if (isMermaid) return children;

    return (
      <div className={markdownClasses.content.codeBlock}>
        <pre>{children}</pre>
      </div>
    );
  },
  code({ className, children, node, ...other }) {
    const language = /language-(\w+)/.exec(className || '');

    if (language?.[1] === 'mermaid') {
      return <MermaidDiagram chart={`${children}`.replace(/\n$/, '')} />;
    }

    return language ? (
      <code {...other} className={className}>
        {children}
      </code>
    ) : (
      <code {...other} className={markdownClasses.content.codeInline}>
        {children}
      </code>
    );
  },
};
